import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { ChevronLeft, ChevronRight, Star, Flame, Droplets, Footprints, Mountain, Bike, Waves, Dumbbell, Moon, Zap, BookOpen, Target, Clock, CalendarDays, Check, Circle, Plus, Trash2, X, Shoe } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_CONFIG = {
  fuerza:    { bg: 'bg-amber-500',  ring: 'ring-amber-500/40',  text: 'text-amber-400',  label: 'Fuerza',    icon: Dumbbell,   gradient: 'from-amber-500/20 to-amber-900/10' },
  carrera:   { bg: 'bg-blue-500',   ring: 'ring-blue-500/40',   text: 'text-blue-400',   label: 'Carrera',   icon: Footprints, gradient: 'from-blue-500/20 to-blue-900/10' },
  camino:    { bg: 'bg-lime-500',   ring: 'ring-lime-500/40',   text: 'text-lime-400',   label: 'Camino',    icon: Mountain,   gradient: 'from-lime-500/20 to-lime-900/10' },
  bike:      { bg: 'bg-orange-500', ring: 'ring-orange-500/40', text: 'text-orange-400', label: 'Bike',      icon: Bike,       gradient: 'from-orange-500/20 to-orange-900/10' },
  funcional: { bg: 'bg-yellow-500', ring: 'ring-yellow-500/40', text: 'text-yellow-400', label: 'Funcional', icon: Zap,        gradient: 'from-yellow-500/20 to-yellow-900/10' },
  excursion: { bg: 'bg-green-500',  ring: 'ring-green-500/40',  text: 'text-green-400',  label: 'Excursion', icon: Mountain,   gradient: 'from-green-500/20 to-green-900/10' },
  natacion:  { bg: 'bg-cyan-500',   ring: 'ring-cyan-500/40',   text: 'text-cyan-400',   label: 'Natacion',  icon: Waves,      gradient: 'from-cyan-500/20 to-cyan-900/10' },
  especial:  { bg: 'bg-pink-500',   ring: 'ring-pink-500/40',   text: 'text-pink-400',   label: 'Especial',  icon: Star,       gradient: 'from-pink-500/20 to-pink-900/10' },
  descanso:  { bg: 'bg-gray-600',   ring: 'ring-gray-600/40',   text: 'text-gray-500',   label: 'Descanso',  icon: Moon,       gradient: 'from-gray-600/20 to-gray-900/10' },
  evento:    { bg: 'bg-gold',       ring: 'ring-gold/40',       text: 'text-gold',       label: 'Evento',    icon: Star,       gradient: 'from-gold/20 to-amber-900/10' },
};

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAY_NAMES = ['L','M','X','J','V','S','D'];

// Tipos que requieren calzado + km
const TIPOS_CALZADO = ['carrera', 'excursion', 'camino'];

export default function CalendarioPreparacion() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [planes, setPlanes] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showRecos, setShowRecos] = useState(false);
  const [activeTab, setActiveTab] = useState('calendario');
  const [checkins, setCheckins] = useState([]);
  const [extras, setExtras] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newExtra, setNewExtra] = useState({ tipo: 'fuerza', descripcion: '' });
  const [calzado, setCalzado] = useState([]);
  const [showCalzadoForm, setShowCalzadoForm] = useState(false);
  const [newCalzado, setNewCalzado] = useState('');
  const [pendingCheck, setPendingCheck] = useState(null);
  const [checkKm, setCheckKm] = useState('');
  const [checkCalzadoId, setCheckCalzadoId] = useState('');

  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const mes = `${year}-${String(month + 1).padStart(2, '0')}`;

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [planesRes, calRes, checkinsRes, extrasRes, calzadoRes] = await Promise.all([
        axios.get(`${API}/planes`, { headers }),
        axios.get(`${API}/planes/calendario/${user.id}?mes=${mes}`, { headers }),
        axios.get(`${API}/planes/checkins/${user.id}`, { headers }),
        axios.get(`${API}/planes/extras/${user.id}?mes=${mes}`, { headers }),
        axios.get(`${API}/calzado`, { headers })
      ]);
      setPlanes(planesRes.data);
      setEventos(calRes.data);
      setCheckins(checkinsRes.data);
      setExtras(extrasRes.data);
      setCalzado(calzadoRes.data);
    } catch (e) { console.error(e); }
  }, [mes, user?.id, headers]);

  useEffect(() => { loadData(); }, [loadData]);

  const plan = planes.find(p => p.cliente_id === user?.id) || planes[0];

  // Checkin helpers
  const isChecked = useCallback((fecha, tipo, descripcion) => {
    return checkins.some(c => c.fecha === fecha && c.tipo === tipo && c.descripcion === descripcion);
  }, [checkins]);

  // Extract km from description (e.g., "Caminata: 8 km" → 8)
  const extractKm = (desc) => {
    const match = desc.match(/(\d+[\.,]?\d*)\s*km/i);
    return match ? match[1].replace(',', '.') : '';
  };

  const toggleCheckin = async (ev) => {
    const checked = isChecked(ev.fecha, ev.tipo, ev.descripcion);
    
    // If unchecking, just toggle directly
    if (checked) {
      try {
        const res = await axios.post(`${API}/planes/checkin`, {
          plan_id: ev.plan_id,
          fecha: ev.fecha,
          tipo: ev.tipo,
          descripcion: ev.descripcion
        }, { headers });
        if (!res.data.completado) {
          setCheckins(prev => prev.filter(c => !(c.fecha === ev.fecha && c.tipo === ev.tipo && c.descripcion === ev.descripcion)));
          loadData(); // refresh calzado km
        }
      } catch (e) { console.error(e); }
      return;
    }
    
    // If checking a running/walking activity → show km + calzado dialog
    if (TIPOS_CALZADO.includes(ev.tipo)) {
      setPendingCheck(ev);
      setCheckKm(extractKm(ev.descripcion));
      setCheckCalzadoId(calzado.length > 0 ? calzado[0].id : '');
      return;
    }
    
    // Otherwise, check directly
    try {
      const res = await axios.post(`${API}/planes/checkin`, {
        plan_id: ev.plan_id,
        fecha: ev.fecha,
        tipo: ev.tipo,
        descripcion: ev.descripcion
      }, { headers });
      if (res.data.completado) {
        setCheckins(prev => [...prev, { plan_id: ev.plan_id, fecha: ev.fecha, tipo: ev.tipo, descripcion: ev.descripcion }]);
      }
    } catch (e) { console.error(e); }
  };

  const confirmCheck = async () => {
    if (!pendingCheck) return;
    try {
      const res = await axios.post(`${API}/planes/checkin`, {
        plan_id: pendingCheck.plan_id,
        fecha: pendingCheck.fecha,
        tipo: pendingCheck.tipo,
        descripcion: pendingCheck.descripcion,
        calzado_id: checkCalzadoId || null,
        km: checkKm ? parseFloat(checkKm) : null
      }, { headers });
      if (res.data.completado) {
        setCheckins(prev => [...prev, {
          plan_id: pendingCheck.plan_id,
          fecha: pendingCheck.fecha,
          tipo: pendingCheck.tipo,
          descripcion: pendingCheck.descripcion,
          calzado_id: checkCalzadoId,
          km: checkKm ? parseFloat(checkKm) : null
        }]);
        loadData(); // refresh calzado km
      }
      setPendingCheck(null);
    } catch (e) { console.error(e); }
  };

  const createCalzado = async () => {
    if (!newCalzado.trim()) return;
    try {
      const res = await axios.post(`${API}/calzado`, { nombre: newCalzado.trim() }, { headers });
      setCalzado(prev => [res.data, ...prev]);
      setNewCalzado('');
      setShowCalzadoForm(false);
      if (!checkCalzadoId) setCheckCalzadoId(res.data.id);
    } catch (e) { console.error(e); }
  };

  const deleteCalzado = async (id) => {
    try {
      await axios.delete(`${API}/calzado/${id}`, { headers });
      setCalzado(prev => prev.filter(c => c.id !== id));
    } catch (e) { console.error(e); }
  };

  // Calculate countdown to event
  const countdown = useMemo(() => {
    if (!plan?.evento?.fecha_inicio) return null;
    const eventDate = new Date(plan.evento.fecha_inicio + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    return diff;
  }, [plan]);

  // Calculate progress based on actual checkins
  const progress = useMemo(() => {
    if (!plan?.fecha_inicio || !plan?.num_semanas) return null;
    const start = new Date(plan.fecha_inicio + 'T00:00:00');
    const today = new Date();
    const elapsed = Math.floor((today - start) / (1000 * 60 * 60 * 24));
    const currentWeek = Math.max(1, Math.min(plan.num_semanas, Math.ceil(Math.max(1, elapsed) / 7)));
    
    // Count total non-rest activities and completed ones
    let totalActivities = 0;
    plan.semanas?.forEach(s => s.dias?.forEach(d => d.actividades?.forEach(a => {
      if (a.tipo !== 'descanso') totalActivities++;
    })));
    const completedCount = checkins.filter(c => c.plan_id === plan.id).length;
    const pct = totalActivities > 0 ? Math.round((completedCount / totalActivities) * 100) : 0;
    
    return { pct, currentWeek, completedCount, totalActivities };
  }, [plan, checkins]);

  // Stats: km totales y desglose por tipo
  const stats = useMemo(() => {
    const totalKm = checkins.reduce((sum, c) => sum + (c.km || 0), 0);
    const byType = {};
    checkins.forEach(c => {
      if (!byType[c.tipo]) byType[c.tipo] = 0;
      byType[c.tipo]++;
    });
    return { totalKm, byType };
  }, [checkins]);

  // Weekly evolution: checkins + km per week
  const weeklyEvolution = useMemo(() => {
    if (!plan?.fecha_inicio || !plan?.semanas?.length) return [];
    const start = new Date(plan.fecha_inicio + 'T00:00:00');
    return plan.semanas.map(sem => {
      let weekCheckins = 0;
      let weekKm = 0;
      let totalPlanned = 0;
      sem.dias?.forEach(d => {
        d.actividades?.forEach(a => {
          if (a.tipo !== 'descanso') totalPlanned++;
        });
      });
      // Match checkins to this week by date range
      const weekStart = new Date(start);
      weekStart.setDate(weekStart.getDate() + (sem.numero - 1) * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const ws = weekStart.toISOString().slice(0, 10);
      const we = weekEnd.toISOString().slice(0, 10);
      checkins.forEach(c => {
        if (c.fecha >= ws && c.fecha <= we) {
          weekCheckins++;
          weekKm += c.km || 0;
        }
      });
      return { numero: sem.numero, nombre: sem.nombre, checkins: weekCheckins, km: Math.round(weekKm * 10) / 10, planned: totalPlanned };
    });
  }, [plan, checkins]);

  // Calendar helpers
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();

  const getEventosForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const planEvents = eventos.filter(e => e.fecha === dateStr);
    const extraEvents = extras.filter(e => e.fecha === dateStr).map(e => ({
      ...e, plan_id: e.plan_id || plan?.id, plan_nombre: plan?.nombre || '', semana: 0, extra: true
    }));
    return [...planEvents, ...extraEvents];
  };

  const addExtra = async () => {
    if (!selectedDay || !newExtra.descripcion.trim()) return;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay.day).padStart(2, '0')}`;
    try {
      const res = await axios.post(`${API}/planes/extras`, {
        plan_id: plan?.id || '',
        fecha: dateStr,
        tipo: newExtra.tipo,
        descripcion: newExtra.descripcion.trim()
      }, { headers });
      setExtras(prev => [...prev, res.data]);
      setSelectedDay(prev => ({
        ...prev,
        eventos: [...prev.eventos, { ...res.data, semana: 0, extra: true }]
      }));
      setNewExtra({ tipo: 'fuerza', descripcion: '' });
      setShowAddForm(false);
    } catch (e) { console.error(e); }
  };

  const deleteExtra = async (extraId) => {
    try {
      await axios.delete(`${API}/planes/extras/${extraId}`, { headers });
      setExtras(prev => prev.filter(e => e.id !== extraId));
      setSelectedDay(prev => ({
        ...prev,
        eventos: prev.eventos.filter(e => e.id !== extraId)
      }));
    } catch (e) { console.error(e); }
  };

  const isToday = (day) => {
    const now = new Date();
    return now.getDate() === day && now.getMonth() === month && now.getFullYear() === year;
  };

  // No plan state
  if (!plan && planes.length === 0) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-dark-mk flex flex-col items-center justify-center p-6" data-testid="calendario-preparacion">
          <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center mb-4">
            <CalendarDays className="h-10 w-10 text-gray-600" />
          </div>
          <h2 className="text-white text-xl font-bold mb-2">Calendario Preparacion</h2>
          <p className="text-gray-500 text-sm text-center max-w-xs">
            Aun no tienes un plan de entrenamiento asignado. Cuando tu entrenador cree uno, aparecera aqui.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-dark-mk" data-testid="calendario-preparacion">

        {/* ── HERO SECTION ── */}
        <div className="relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-gold/5 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-gold/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative px-4 pt-6 pb-4">
            {/* Title */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold/30 to-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                <Target className="h-5 w-5 text-gold" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white leading-tight" data-testid="plan-title">{plan?.nombre || 'Calendario Preparacion'}</h1>
                <p className="text-gray-400 text-xs mt-0.5">{plan?.descripcion}</p>
              </div>
            </div>

            {/* Event countdown + progress row */}
            <div className="flex gap-3 mb-4">
              {/* Countdown */}
              {countdown !== null && (
                <div className="flex-1 bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/20 rounded-xl p-3" data-testid="event-countdown">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="h-3.5 w-3.5 text-gold" />
                    <span className="text-gold text-[10px] font-bold uppercase tracking-wider">{plan.evento.nombre}</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-white tabular-nums">{Math.abs(countdown)}</span>
                    <span className="text-gray-400 text-xs">{countdown > 0 ? 'dias' : countdown === 0 ? 'HOY' : 'dias atras'}</span>
                  </div>
                  {countdown > 0 && (
                    <div className="w-full bg-gray-800 rounded-full h-1 mt-2">
                      <div className="bg-gold rounded-full h-1 transition-all duration-500" style={{ width: `${progress ? progress.pct : 0}%` }} />
                    </div>
                  )}
                </div>
              )}

              {/* Progress */}
              {progress && (
                <div className="flex-1 bg-gray-900/80 border border-gray-800 rounded-xl p-3" data-testid="plan-progress">
                  <div className="flex items-center gap-2 mb-1">
                    <Flame className="h-3.5 w-3.5 text-amber-400" />
                    <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">Progreso</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-white tabular-nums">{progress.pct}%</span>
                  </div>
                  <p className="text-gray-500 text-[10px] mt-1">{progress.completedCount}/{progress.totalActivities} actividades</p>
                </div>
              )}
            </div>

            {/* Stats: km totales + desglose por tipo */}
            {checkins.length > 0 && (
              <div className="flex gap-3 mb-4" data-testid="hero-stats">
                {/* Total Km */}
                {stats.totalKm > 0 && (
                  <div className="bg-gray-900/80 border border-gray-800 rounded-xl p-3 flex-shrink-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Footprints className="h-3.5 w-3.5 text-blue-400" />
                      <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">Km totales</span>
                    </div>
                    <span className="text-2xl font-black text-white tabular-nums" data-testid="total-km">{Math.round(stats.totalKm * 10) / 10}</span>
                  </div>
                )}

                {/* Desglose por tipo */}
                <div className="flex-1 bg-gray-900/80 border border-gray-800 rounded-xl p-3 min-w-0">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Zap className="h-3.5 w-3.5 text-yellow-400" />
                    <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">Actividades</span>
                    <span className="ml-auto text-white text-xs font-bold tabular-nums" data-testid="total-checkins">{checkins.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(stats.byType).sort((a, b) => b[1] - a[1]).map(([tipo, count]) => {
                      const tc = TIPO_CONFIG[tipo] || TIPO_CONFIG.especial;
                      const Icon = tc.icon;
                      return (
                        <span key={tipo} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-gradient-to-r ${tc.gradient} ${tc.text} border border-current/10`}
                          data-testid={`stat-type-${tipo}`}>
                          <Icon className="h-3 w-3" />
                          {tc.label} {count}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Tabs: Calendario | Recomendaciones | Calzado */}
            <div className="flex bg-gray-900/80 rounded-lg p-0.5 border border-gray-800" data-testid="tabs">
              <button onClick={() => setActiveTab('calendario')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-all ${
                  activeTab === 'calendario' ? 'bg-gold/20 text-gold' : 'text-gray-500 hover:text-gray-300'
                }`} data-testid="tab-calendario">
                <CalendarDays className="h-3.5 w-3.5" /> Calendario
              </button>
              {plan?.recomendaciones?.length > 0 && (
                <button onClick={() => setActiveTab('recomendaciones')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-all ${
                    activeTab === 'recomendaciones' ? 'bg-gold/20 text-gold' : 'text-gray-500 hover:text-gray-300'
                  }`} data-testid="tab-recomendaciones">
                  <BookOpen className="h-3.5 w-3.5" /> Recos
                  <span className="bg-gold/30 text-gold text-[9px] font-bold px-1.5 py-0.5 rounded-full">{plan.recomendaciones.length}</span>
                </button>
              )}
              <button onClick={() => setActiveTab('calzado')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-all ${
                  activeTab === 'calzado' ? 'bg-gold/20 text-gold' : 'text-gray-500 hover:text-gray-300'
                }`} data-testid="tab-calzado">
                <Footprints className="h-3.5 w-3.5" /> Calzado
                {calzado.length > 0 && <span className="bg-gold/30 text-gold text-[9px] font-bold px-1.5 py-0.5 rounded-full">{calzado.length}</span>}
              </button>
            </div>
          </div>
        </div>

        {/* ── CALENDAR TAB ── */}
        {activeTab === 'calendario' && (
          <div className="px-4 pb-24">
            {/* Month nav */}
            <div className="flex items-center justify-between my-3">
              <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                className="w-8 h-8 rounded-lg bg-gray-800/60 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" data-testid="prev-month">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <h2 className="text-white font-bold text-base uppercase tracking-wide">
                {MONTH_NAMES[month]} {year}
              </h2>
              <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                className="w-8 h-8 rounded-lg bg-gray-800/60 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" data-testid="next-month">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAY_NAMES.map(d => (
                <div key={d} className="text-center text-[10px] text-gray-600 font-semibold uppercase py-1">{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
              {Array.from({ length: startPad }).map((_, i) => (
                <div key={`pad-${i}`} className="h-14 sm:h-16" />
              ))}

              {Array.from({ length: totalDays }).map((_, i) => {
                const day = i + 1;
                const dayEvs = getEventosForDay(day);
                const hasEvento = dayEvs.some(e => e.tipo === 'evento');
                const today = isToday(day);
                const hasActivity = dayEvs.length > 0;
                const nonRestEvs = dayEvs.filter(e => e.tipo !== 'descanso' && e.tipo !== 'evento');
                const allChecked = nonRestEvs.length > 0 && nonRestEvs.every(e => isChecked(e.fecha, e.tipo, e.descripcion));
                const someChecked = nonRestEvs.some(e => isChecked(e.fecha, e.tipo, e.descripcion));

                return (
                  <button key={day} onClick={() => setSelectedDay({ day, eventos: dayEvs })}
                    className={`h-14 sm:h-16 rounded-lg flex flex-col items-center justify-start pt-1 transition-all relative overflow-hidden ${
                      allChecked && nonRestEvs.length > 0 ? 'bg-green-900/30 border border-green-500/40' :
                      hasEvento ? 'bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/40 shadow-lg shadow-gold/10' :
                      hasActivity ? 'bg-gray-800/60 border border-gray-700/60 hover:border-gray-600' :
                      'border border-gray-800/30 hover:border-gray-700/60 hover:bg-gray-800/20'
                    } ${today ? 'ring-2 ring-gold/50' : ''}`}
                    data-testid={`day-${day}`}>

                    <span className={`text-[11px] font-bold ${
                      today ? 'text-gold' :
                      hasEvento ? 'text-gold' :
                      hasActivity ? 'text-white' :
                      'text-gray-700'
                    }`}>{day}</span>

                    {/* Activity dots or check */}
                    {allChecked && nonRestEvs.length > 0 ? (
                      <Check className="h-3.5 w-3.5 text-green-400 mt-0.5" />
                    ) : (
                      <div className="flex flex-wrap gap-px justify-center mt-0.5">
                        {dayEvs.slice(0, 4).map((ev, ei) => {
                          const tc = TIPO_CONFIG[ev.tipo] || TIPO_CONFIG.especial;
                          const checked = isChecked(ev.fecha, ev.tipo, ev.descripcion);
                          return <div key={ei} className={`w-2 h-2 rounded-full ${checked ? 'bg-green-400' : tc.bg}`} />;
                        })}
                        {dayEvs.length > 4 && (
                          <span className="text-[7px] text-gray-500 font-bold">+{dayEvs.length - 4}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-4 justify-center">
              {Object.entries(TIPO_CONFIG).filter(([k]) => k !== 'descanso').map(([tipo, tc]) => {
                const Icon = tc.icon;
                return (
                  <div key={tipo} className="flex items-center gap-1">
                    <Icon className={`h-2.5 w-2.5 ${tc.text}`} />
                    <span className="text-gray-500 text-[10px] capitalize">{tc.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Weekly evolution chart */}
            {weeklyEvolution.length > 0 && (
              <div className="mt-6 mb-2" data-testid="weekly-evolution">
                <h3 className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-3">Evolucion semanal</h3>
                <div className="bg-gray-900/60 border border-gray-800/60 rounded-xl p-3 space-y-2.5">
                  {(() => {
                    const maxCheckins = Math.max(...weeklyEvolution.map(w => w.planned), 1);
                    const maxKm = Math.max(...weeklyEvolution.map(w => w.km), 1);
                    const hasAnyKm = weeklyEvolution.some(w => w.km > 0);
                    return weeklyEvolution.map(w => {
                      const isCurrent = progress?.currentWeek === w.numero;
                      const completionPct = w.planned > 0 ? Math.round((w.checkins / w.planned) * 100) : 0;
                      return (
                        <div key={w.numero} className="group" data-testid={`evo-week-${w.numero}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse flex-shrink-0" />}
                            <span className={`text-[10px] font-bold ${isCurrent ? 'text-gold' : 'text-gray-500'}`}>S{w.numero}</span>
                            <span className="text-[10px] text-gray-600 ml-auto tabular-nums">{w.checkins}/{w.planned}</span>
                            {hasAnyKm && w.km > 0 && (
                              <span className="text-[10px] text-blue-400 tabular-nums font-medium">{w.km} km</span>
                            )}
                          </div>
                          {/* Activity bar */}
                          <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                            <div className={`h-2 rounded-full transition-all duration-700 ${
                              completionPct >= 100 ? 'bg-green-500' :
                              completionPct >= 50 ? 'bg-gold' :
                              w.checkins > 0 ? 'bg-amber-600' : 'bg-gray-700'
                            }`}
                              style={{ width: `${w.planned > 0 ? Math.max(w.checkins > 0 ? 8 : 0, (w.checkins / maxCheckins) * 100) : 0}%` }} />
                          </div>
                          {/* Km bar (only if any week has km) */}
                          {hasAnyKm && (
                            <div className="w-full bg-gray-800/50 rounded-full h-1.5 mt-1 overflow-hidden">
                              <div className="h-1.5 rounded-full bg-blue-500/70 transition-all duration-700"
                                style={{ width: `${w.km > 0 ? Math.max(8, (w.km / maxKm) * 100) : 0}%` }} />
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                  {/* Legend */}
                  <div className="flex items-center gap-4 pt-1 border-t border-gray-800/60">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-2 rounded-full bg-gold" />
                      <span className="text-[9px] text-gray-500">Actividades</span>
                    </div>
                    {weeklyEvolution.some(w => w.km > 0) && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-1.5 rounded-full bg-blue-500/70" />
                        <span className="text-[9px] text-gray-500">Kilometros</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Week summary below calendar */}
            {plan?.semanas?.length > 0 && progress && (
              <div className="mt-6">
                <h3 className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-3">Resumen por semanas</h3>
                <div className="space-y-2">
                  {plan.semanas.map((sem, si) => {
                    const isCurrent = progress.currentWeek === sem.numero;
                    const isPast = progress.currentWeek > sem.numero;
                    return (
                      <div key={si} className={`rounded-xl border p-3 transition-all ${
                        isCurrent ? 'bg-gold/5 border-gold/30' :
                        isPast ? 'bg-gray-900/50 border-gray-800/50 opacity-60' :
                        'bg-gray-900/30 border-gray-800/50'
                      }`} data-testid={`week-${sem.numero}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {isCurrent && <div className="w-2 h-2 rounded-full bg-gold animate-pulse" />}
                            <span className={`text-xs font-bold ${isCurrent ? 'text-gold' : 'text-gray-400'}`}>
                              {sem.nombre || `Semana ${sem.numero}`}
                            </span>
                          </div>
                          {isCurrent && <span className="text-[9px] text-gold bg-gold/10 px-2 py-0.5 rounded-full font-bold">ACTUAL</span>}
                          {isPast && <span className="text-[9px] text-gray-600 font-medium">Completada</span>}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {sem.dias?.map((dia, di) => (
                            <React.Fragment key={di}>
                              {dia.actividades?.map((act, ai) => {
                                const tc = TIPO_CONFIG[act.tipo] || TIPO_CONFIG.especial;
                                return (
                                  <span key={ai} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-gradient-to-r ${tc.gradient} ${tc.text} border border-current/10`}>
                                    {tc.label}
                                  </span>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── RECOMENDACIONES TAB ── */}
        {activeTab === 'recomendaciones' && plan?.recomendaciones?.length > 0 && (
          <div className="px-4 pb-24 pt-4">
            <div className="space-y-3">
              {plan.recomendaciones.map((r, i) => {
                const icons = [Droplets, Footprints, Flame, Mountain, Dumbbell, Waves, Star, Zap];
                const Icon = icons[i % icons.length];
                const colors = ['text-cyan-400', 'text-green-400', 'text-amber-400', 'text-emerald-400', 'text-orange-400', 'text-blue-400', 'text-gold', 'text-yellow-400'];
                const bgColors = ['from-cyan-500/10', 'from-green-500/10', 'from-amber-500/10', 'from-emerald-500/10', 'from-orange-500/10', 'from-blue-500/10', 'from-gold/10', 'from-yellow-500/10'];

                return (
                  <div key={i} className={`bg-gradient-to-r ${bgColors[i % bgColors.length]} to-transparent border border-gray-800/60 rounded-xl p-4`}
                    data-testid={`reco-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg bg-gray-800/80 flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`h-4 w-4 ${colors[i % colors.length]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-bold">{r.titulo}</h4>
                        <p className="text-gray-400 text-xs mt-1 leading-relaxed">{r.texto}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── CALZADO TAB ── */}
        {activeTab === 'calzado' && (
          <div className="px-4 pb-24 pt-4">
            {/* Summary */}
            {calzado.length > 0 && (
              <div className="space-y-3 mb-6">
                {calzado.map(c => {
                  const pctLife = Math.min(100, Math.round((c.km_total / (c.km_alerta || 500)) * 100));
                  const isWorn = pctLife >= 80;
                  return (
                    <div key={c.id} className={`rounded-xl p-4 border ${isWorn ? 'bg-red-900/10 border-red-500/30' : 'bg-gray-900/50 border-gray-800'}`}
                      data-testid={`calzado-${c.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Footprints className={`h-4 w-4 ${isWorn ? 'text-red-400' : 'text-green-400'}`} />
                          <span className="text-white font-bold text-sm">{c.nombre}</span>
                        </div>
                        <button onClick={() => { if (window.confirm('¿Eliminar este calzado?')) deleteCalzado(c.id); }}
                          className="text-gray-600 hover:text-red-400 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-2xl font-black text-white tabular-nums">{Math.round(c.km_total)}</span>
                        <span className="text-gray-500 text-sm">km</span>
                        <span className="text-gray-600 text-xs ml-auto">de {c.km_alerta || 500} km</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div className={`rounded-full h-2 transition-all ${isWorn ? 'bg-red-500' : pctLife >= 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(100, pctLife)}%` }} />
                      </div>
                      {isWorn && (
                        <p className="text-red-400 text-[10px] font-bold mt-1.5 uppercase">Calzado con mucho uso - considera cambiarlo</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Add calzado */}
            {!showCalzadoForm ? (
              <button onClick={() => setShowCalzadoForm(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-gray-600 text-gray-400 hover:border-gold/50 hover:text-gold transition-all"
                data-testid="btn-add-calzado">
                <Plus className="h-4 w-4" />
                <span className="text-sm font-medium">Añadir calzado</span>
              </button>
            ) : (
              <div className="bg-gray-800/60 rounded-xl p-3 border border-gray-700/60 space-y-2">
                <input type="text" value={newCalzado}
                  onChange={e => setNewCalzado(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createCalzado()}
                  placeholder="Nombre del calzado (ej: Salomon X Ultra)"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-gold/50 focus:outline-none"
                  autoFocus
                  data-testid="input-calzado-nombre" />
                <div className="flex gap-2">
                  <button onClick={createCalzado}
                    disabled={!newCalzado.trim()}
                    className="flex-1 bg-gold/20 text-gold border border-gold/30 rounded-lg py-2 text-sm font-bold hover:bg-gold/30 transition-all disabled:opacity-40"
                    data-testid="btn-save-calzado">
                    Guardar
                  </button>
                  <button onClick={() => { setShowCalzadoForm(false); setNewCalzado(''); }}
                    className="px-4 py-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white text-sm">
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {calzado.length === 0 && !showCalzadoForm && (
              <p className="text-gray-600 text-xs text-center mt-4">Añade tu calzado para hacer seguimiento de kilometros</p>
            )}
          </div>
        )}

        {/* ── KM + CALZADO CONFIRMATION DIALOG ── */}
        <Dialog open={!!pendingCheck} onOpenChange={() => setPendingCheck(null)}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            {pendingCheck && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-base">Registrar actividad</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div className="bg-gray-800/60 rounded-xl p-3 border border-gray-700/60">
                    <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">{(TIPO_CONFIG[pendingCheck.tipo] || {}).label}</p>
                    <p className="text-white text-sm">{pendingCheck.descripcion}</p>
                  </div>

                  <div>
                    <label className="text-gray-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Kilometros</label>
                    <input type="number" value={checkKm}
                      onChange={e => setCheckKm(e.target.value)}
                      placeholder="0"
                      step="0.1"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-lg text-white font-bold tabular-nums focus:border-gold/50 focus:outline-none"
                      data-testid="input-check-km" />
                  </div>

                  <div>
                    <label className="text-gray-400 text-xs font-bold uppercase tracking-wider block mb-1.5">Calzado</label>
                    {calzado.length > 0 ? (
                      <select value={checkCalzadoId}
                        onChange={e => setCheckCalzadoId(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-gold/50 focus:outline-none"
                        data-testid="select-check-calzado">
                        <option value="">Sin calzado</option>
                        {calzado.map(c => (
                          <option key={c.id} value={c.id}>{c.nombre} ({Math.round(c.km_total)} km)</option>
                        ))}
                      </select>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-gray-500 text-xs">No tienes calzado registrado</p>
                        <div className="flex gap-2">
                          <input type="text" value={newCalzado}
                            onChange={e => setNewCalzado(e.target.value)}
                            placeholder="Nombre del calzado..."
                            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-gold/50 focus:outline-none"
                            data-testid="input-quick-calzado" />
                          <button onClick={createCalzado}
                            disabled={!newCalzado.trim()}
                            className="px-3 py-2 bg-gold/20 text-gold rounded-lg text-sm font-bold disabled:opacity-40">
                            +
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <button onClick={confirmCheck}
                    className="w-full bg-green-600 text-white rounded-lg py-3 text-sm font-bold hover:bg-green-500 transition-all flex items-center justify-center gap-2"
                    data-testid="btn-confirm-check">
                    <Check className="h-4 w-4" />
                    Confirmar
                  </button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ── DAY DETAIL DIALOG ── */}
        <Dialog open={!!selectedDay} onOpenChange={() => { setSelectedDay(null); setShowAddForm(false); }}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-sm">
            {selectedDay && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-lg pr-8">
                    {selectedDay.day} {MONTH_NAMES[month]} {year}
                  </DialogTitle>
                </DialogHeader>

                {/* Activities list */}
                <div className="space-y-2 mt-1">
                  {selectedDay.eventos.length === 0 && !showAddForm && (
                    <p className="text-gray-500 text-sm text-center py-4">Sin actividades. Pulsa + para añadir.</p>
                  )}
                  {selectedDay.eventos.map((ev, i) => {
                    const tc = TIPO_CONFIG[ev.tipo] || TIPO_CONFIG.especial;
                    const Icon = tc.icon;
                    const isEvento = ev.tipo === 'evento';
                    const isRest = ev.tipo === 'descanso';
                    const isExtra = ev.extra;
                    const checked = isChecked(ev.fecha, ev.tipo, ev.descripcion);
                    const canCheck = !isEvento && !isRest;
                    return (
                      <div key={i} className={`rounded-xl p-3 transition-all ${
                        checked ? 'bg-green-900/20 border border-green-500/30' :
                        isExtra ? 'bg-gradient-to-r from-purple-500/10 to-transparent border border-purple-500/30' :
                        isEvento ? 'bg-gradient-to-r from-gold/10 to-transparent border border-gold/30' :
                        `bg-gradient-to-r ${tc.gradient} border border-gray-700/60`
                      }`}>
                        <div className="flex items-center gap-2">
                          {/* Check button */}
                          {canCheck && (
                            <button onClick={() => toggleCheckin(ev)}
                              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                                checked 
                                  ? 'bg-green-500 text-white' 
                                  : 'bg-gray-800 border border-gray-600 text-gray-600 hover:border-gray-400'
                              }`}
                              data-testid={`check-${ev.tipo}-${i}`}>
                              {checked ? <Check className="h-4 w-4" /> : <Circle className="h-3 w-3" />}
                            </button>
                          )}
                          {!canCheck && (
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isEvento ? 'bg-gold/20' : 'bg-gray-800/80'}`}>
                              <Icon className={`h-3.5 w-3.5 ${tc.text}`} />
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-bold uppercase tracking-wider ${checked ? 'text-green-400' : tc.text}`}>{tc.label}</span>
                              {isExtra && <span className="text-[8px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded-full font-bold">EXTRA</span>}
                              {ev.semana > 0 && (
                                <span className="text-gray-600 text-[10px] ml-auto">Sem. {ev.semana}</span>
                              )}
                            </div>
                            <p className={`text-sm font-medium ${
                              checked ? 'text-green-300/80 line-through' :
                              isEvento ? 'text-gold' : 'text-white'
                            }`}>
                              {ev.descripcion}
                            </p>
                          </div>

                          {/* Delete extra button */}
                          {isExtra && (
                            <button onClick={() => deleteExtra(ev.id)}
                              className="w-6 h-6 rounded-md flex items-center justify-center text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                              data-testid={`delete-extra-${i}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add extra button + form at bottom, well separated from X */}
                {!showAddForm ? (
                  <button onClick={() => setShowAddForm(true)}
                    className="w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-gray-600 text-gray-400 hover:border-gold/50 hover:text-gold transition-all"
                    data-testid="add-extra-btn">
                    <Plus className="h-4 w-4" />
                    <span className="text-sm font-medium">Añadir entreno extra</span>
                  </button>
                ) : (
                  <div className="mt-3 bg-gray-800/60 rounded-xl p-3 border border-gray-700/60 space-y-2" data-testid="add-extra-form">
                    <select value={newExtra.tipo} onChange={e => setNewExtra(p => ({ ...p, tipo: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-gold/50 focus:outline-none"
                      data-testid="extra-tipo-select">
                      {Object.entries(TIPO_CONFIG).filter(([k]) => k !== 'evento' && k !== 'descanso').map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <input type="text" value={newExtra.descripcion}
                      onChange={e => setNewExtra(p => ({ ...p, descripcion: e.target.value }))}
                      placeholder="Descripcion del entreno..."
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-gold/50 focus:outline-none"
                      onKeyDown={e => e.key === 'Enter' && addExtra()}
                      data-testid="extra-desc-input" />
                    <div className="flex gap-2">
                      <button onClick={addExtra}
                        disabled={!newExtra.descripcion.trim()}
                        className="flex-1 bg-gold/20 text-gold border border-gold/30 rounded-lg py-2 text-sm font-bold hover:bg-gold/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="extra-save-btn">
                        Añadir
                      </button>
                      <button onClick={() => { setShowAddForm(false); setNewExtra({ tipo: 'fuerza', descripcion: '' }); }}
                        className="px-4 py-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white text-sm"
                        data-testid="extra-cancel-btn">
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
